import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { USER_CHANGE_PASSWORD_REQUEST } from "../store/Actions/Actions";
import "../styles/ChangePassword.css";
import Toast from "../utils/Toast";

function ChangePassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ current: "", new: "", confirm: "" });
  const [errors, setErrors] = useState({ current: "", new: "", confirm: "" });
  const dispatch = useDispatch();
  const isLoading = useSelector((_) => _?.changePassword?.isLoading);
  const changeError = useSelector((_) => _?.changePassword?.error);
  const changeSuccess = useSelector((_) => _?.changePassword?.success);

  const [toast, setToast] = useState({ message: "", type: "" });

  useEffect(() => {
    if (changeError) {
      setToast({ message: "Invalid current password. or unrecognized error.", type: "failed" });
    }
  }, [changeError]);

  useEffect(() => {
    if (changeSuccess) {
      setToast({ message: "Password changed successfully!", type: "success" });
      setForm({ current: "", new: "", confirm: "" });
    }
  }, [changeSuccess]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // clear field error while typing
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateField = (name, value) => {
    if (!value || value.trim() === "") {
      return "This field is required.";
    }

    if (name === "new") {
      const checks = [];
      if (value.length < 8) checks.push("at least 8 characters");
      if (!/[a-z]/.test(value)) checks.push("a lowercase letter");
      if (!/[A-Z]/.test(value)) checks.push("an uppercase letter");
      if (!/[0-9]/.test(value)) checks.push("a number");
      if (checks.length) return `Include ${checks.join(", ")}.`;
    }

    if (name === "confirm") {
      if (value !== form.new) return "Confirmation does not match the new password.";
    }

    return "";
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors({ ...errors, [name]: error });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // validate all fields
    const nextErrors = {
      current: validateField("current", form.current),
      new: validateField("new", form.new),
      confirm: validateField("confirm", form.confirm),
    };
    setErrors(nextErrors);

    // stop if any errors
    if (Object.values(nextErrors).some((v) => v)) return;

    dispatch({
      type: USER_CHANGE_PASSWORD_REQUEST,
      payload: { current: form.current, new: form.new },
    });
  };

  return (
    <div>
      <p style={{ marginBottom: 16, fontSize: 18 }}>Change Password</p>
      <p style={{ fontSize: 13, color: "#555", marginBottom: 24 }}>
        Update your password regularly to keep your account secure.
        <br />
        Please enter your current password, then create a new one following your
        organization's password policy.
      </p>
      <form className="change-password-form" onSubmit={handleSubmit}>
        <div className="mb-5">
          <label className="form-label fw-normal">Current Password</label>
          <input
            className="form-control"
            type="password"
            name="current"
            value={form.current}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Current Password"
            autoComplete="current-password"
          />
          {errors.current && (
            <div className="text-danger small mt-1">{errors.current}</div>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label fw-normal">New Password</label>
          <input
            className="form-control"
            type="password"
            name="new"
            value={form.new}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="new-password"
          />
          {errors.new && <div className="text-danger small mt-1">{errors.new}</div>} 
        </div>
        <div className="mb-3">
          <label className="form-label fw-normal">Confirm New Password</label>
          <div className="input-group">
            <input
              className="form-control"
              type="password"
              name="confirm"
              value={form.confirm}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="new-password"
            />
          </div>
          {errors.confirm && (
            <div className="text-danger small mt-1">{errors.confirm}</div>
          )}
        </div>
        <button
          className="btn btn-primary w-50 mt-2"
          type="submit"
          disabled={isLoading || Object.values(errors).some((v) => v) || !form.current || !form.new || !form.confirm}
        >
          {isLoading ? "Changing..." : "Change Password"}
        </button>
        <Toast message={toast.message} type={toast.type} />
      </form>
    </div>
  );
}

export default ChangePassword;
